import React, { FC } from 'react';

import { styled } from '@mui/material';
import { Link, LinkProps } from 'react-router-dom';

import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { buildSubscriptionRoute } from '~/routes';
import { css } from '~/styled';
import { Pricing } from '~/types';
import { ComponentVariants } from '~/ui/util/types';

type ButtonVariant = 'default' | 'sticky';

type ChargebeePlanId = Pricing['chargebee_plan_id'];

interface SelectPlanButtonProps {
  priceData: Pricing;
  variant?: ButtonVariant;
  onClick?: (planId?: ChargebeePlanId) => void;
}

const SelectPlanButton: FC<SelectPlanButtonProps> = ({
  priceData,
  variant = 'default',
  onClick,
}) => {
  const {
    enterprise: isEnterprise,
    chargebee_plan_id: chargebeePlanId,
    free_trial_period: freeTrialPeriod,
  } = priceData;

  const trialCount = freeTrialPeriod?.count;

  const { t } = useTenantTranslation();
  const { userInfo } = useUserInfoContext();

  function handleSelectPlan(chargebeePlanId?: ChargebeePlanId) {
    onClick?.(chargebeePlanId);
  }

  if (isEnterprise) {
    return (
      <StyledCustomButton component={Link} to={'/book-a-demo'} customVariant={variant}>
        {t('button.bookADemo')}
      </StyledCustomButton>
    );
  }

  if (userInfo) {
    return (
      <StyledCustomButton
        customVariant={variant}
        component={Link}
        to={buildSubscriptionRoute(chargebeePlanId)}
      >
        {!!trialCount ? t('button.startFreeTrial') : t('button.subscribe')}
      </StyledCustomButton>
    );
  }

  return (
    <StyledCustomButton
      customVariant={variant}
      data-testid="submitPlan"
      onClick={() => handleSelectPlan(chargebeePlanId)}
    >
      {!!trialCount ? t('button.startFreeTrial') : t('button.joinNow')}
    </StyledCustomButton>
  );
};

export default SelectPlanButton;

const buttonVariants: ComponentVariants<ButtonVariant> = {
  default: () => css`
    width: 100%;
  `,
  sticky: (theme) => css`
    width: 100%;
    margin-top: 46px;

    ${theme?.breakpoints.up('sm')} {
      width: unset;
      margin-top: 0;
    }

    ${theme?.breakpoints.up('md')} {
      width: 100%;
      max-width: 305px;
    }

    ${theme?.breakpoints.up('lg')} {
      width: unset;
      max-width: unset;
    }
  `,
};

type CustomButtonProps = {
  customVariant?: ButtonVariant;
} & Partial<LinkProps>;

const StyledCustomButton = styled(CustomButton, {
  shouldForwardProp: (prop) => prop !== 'customVariant',
})<CustomButtonProps>`
  text-align: center;

  ${({ customVariant, theme }) =>
    customVariant && buttonVariants[customVariant || 'default'](theme)}
`;
